import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import clsx from "clsx";
import moment from "moment-timezone";
import CustomButton from "@/components/common/buttons/custom-button/custom-button";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    getPlayablePropertyBySlug,
    deletePlayableProperty,
    getSalesData,
    updatePlayableProperty,
    setDailyPlayableProperty,
    getPlayableProperties,
    deletePropertyImage,
    getAgents,
    getUserAgents,
    type UpdatePlayablePropertyPayload,
    type ImageItemApi,
    type Agent,
} from "@/services/PropertyService";
import MoonLoader from "react-spinners/MoonLoader";
import { normalizeMlsId } from "@/helpers/helpers";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import AdminAccessDenied from "../AdminAccessDenied";
import { cstMoment } from "@common/utils/date.utils";

/** One item in the combined image list (existing URL or new file). */
type ImageListItem = { type: "existing"; url: string; id: string } | { type: "new"; file: File; id: string };

type FieldSpec = { label: string; key: string; required?: boolean; multiline?: boolean };

const propertyFields: FieldSpec[] = [
    { label: "Street address", key: "streetAddress", required: true },
    { label: "City", key: "city", required: true },
    { label: "State", key: "state", required: true },
    { label: "Zip code", key: "zipCode", required: true },
    { label: "Neighborhood", key: "neighborhood" },
    { label: "Public remarks", key: "publicRemarks", multiline: true },
    { label: "List price", key: "listPrice", required: true },
    { label: "Bedrooms", key: "bedrooms" },
    { label: "Bathrooms", key: "bathrooms" },
    { label: "Property Type", key: "propertyType" },
    { label: "Square footage", key: "squareFootage" },
    { label: "Parking spots", key: "parkingSpots" },
    { label: "Parking type", key: "parkingType" },
    { label: "Year built", key: "yearBuilt" },
    { label: "Exterior features", key: "exteriorFeatures" },
    { label: "Interior features", key: "interiorFeatures" },
    { label: "Heating", key: "heating" },
    { label: "Cooling", key: "cooling" },
    { label: "Laundry Features", key: "laundryFeatures" },
    { label: "Lot dimensions", key: "lotDimensions" },
    { label: "Listing ID", key: "listingId", required: true },
    { label: "Lat", key: "lat", required: true },
    { label: "Lng", key: "lng", required: true },
    { label: "Property Images", key: "uploadedImages", required: true },
];

const puzzleFields: FieldSpec[] = [
    { label: "Play Date", key: "playDate", required: true },
    // { label: "MLS ID", key: "mlsId", required: true },
    { label: "Long link", key: "longLink", required: true },
    { label: "Image index", key: "imageIndex" },
    { label: "Email subject", key: "emailSubject" },
    { label: "Email image", key: "emailImage" },
    { label: "Email body", key: "emailBody", multiline: true },
    { label: "Feature agent", key: "featureAgent" },
    { label: "External Property Link", key: "externalPropertyLink" },
];

const POST_BODY_MAX_LENGTH = 500;

const agentFields: FieldSpec[] = [
    { label: "Agent First Name", key: "agentFirstName" },
    { label: "Agent Last Name", key: "agentLastName" },
    { label: "Agent Email", key: "agentEmail" },
    { label: "Agent Phone Number", key: "agentPhoneNumber" },
    { label: "Agent Brokerage", key: "agentBrokerage" },
    { label: "Agent Primary Link", key: "agentPrimaryLink" },
    { label: "Agent Image Link", key: "agentImageLink" },
    { label: "Agent Instagram", key: "agentInstagram" },
    { label: "Agent TikTok", key: "agentTiktok" },
    { label: "Agent Twitter", key: "agentTwitter" },
    { label: "Agent Facebook", key: "agentFacebook" },
    { label: "Agent YouTube", key: "agentYoutube" },
    { label: "Agent MLS ID", key: "agentMlsId" },
];

const fieldLabelMap: Record<string, string> = [...propertyFields, ...puzzleFields, ...agentFields].reduce(
    (acc, field) => {
        acc[field.key] = field.label;
        return acc;
    },
    {} as Record<string, string>,
);

const numericFieldKeys = new Set([
    "listPrice",
    "bedrooms",
    "bathrooms",
    "zipCode",
    "squareFootage",
    "parkingSpots",
    "yearBuilt",
    "lat",
    "lng",
    "imageIndex",
    "emailImage",
]);

const urlFieldKeys = new Set(["longLink", "agentPrimaryLink", "agentImageLink", "externalPropertyLink"]);

const emailFieldKeys = new Set(["agentEmail"]);

// Creator interface for user agents (different from Agent)
interface Creator {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    displayName?: string | null;
    nickname?: string | null;
    emailAddress?: string | null;
    type?: string | null;
}

/** Display name for a creator, mirroring the feed card: display name, then first/last, then nickname. */
const creatorLabel = (creator: Creator): string => {
    const displayName = creator.displayName?.trim();
    if (displayName) return displayName;
    const fullName = [creator.firstName, creator.lastName]
        .filter((part) => part && part.trim())
        .join(" ")
        .trim();
    if (fullName) return fullName;
    return creator.nickname?.trim() || "PriceMe";
};

/** Secondary line for a creator: email when known, otherwise the nickname handle. */
const creatorSubLabel = (creator: Creator): string =>
    creator.emailAddress || (creator.nickname ? `@${creator.nickname}` : "");

// Agent Typeahead Component
interface AgentTypeaheadProps {
    value: string;
    onChange: (agentId: string, agentData: Agent | null) => void;
    onToggleNewAgent: () => void;
}

const AgentTypeahead: React.FC<AgentTypeaheadProps> = ({ value, onChange, onToggleNewAgent }) => {
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        const fetchAgents = async () => {
            if (debouncedSearch.trim().length < 2) {
                setAgents([]);
                return;
            }

            setLoading(true);
            try {
                const data = await getAgents({
                    search: debouncedSearch.trim(),
                    limit: 50,
                });
                setAgents(data || []);
            } catch (error) {
                console.error("Failed to fetch agents:", error);
                setAgents([]);
            } finally {
                setLoading(false);
            }
        };
        fetchAgents();
    }, [debouncedSearch]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedAgent = agents.find((a) => a.id === value);

    return (
        <div className="tw-relative" ref={dropdownRef}>
            <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Select Agent</label>
                <input
                    type="text"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder={
                        selectedAgent ? `${selectedAgent.firstName} ${selectedAgent.lastName}` : "Search agents..."
                    }
                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                />
            </div>
            {showDropdown && (
                <div className="tw-absolute tw-z-50 tw-w-full tw-mt-1 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md tw-shadow-lg tw-flex tw-flex-col tw-max-h-60">
                    <div className="tw-overflow-y-auto tw-flex-1">
                        {loading ? (
                            <div className="tw-p-3 tw-text-center tw-text-gray-500">Loading...</div>
                        ) : search.trim().length === 0 ? (
                            <div className="tw-p-3 tw-text-center tw-text-gray-500">Type to search...</div>
                        ) : agents.length === 0 ? (
                            <div className="tw-p-3 tw-text-center tw-text-gray-500">No agents found</div>
                        ) : (
                            <>
                                {agents.map((agent) => {
                                    const isSelected = agent.id === value;
                                    return (
                                        <div
                                            key={agent.id}
                                            onClick={() => {
                                                onChange(agent.id, agent);
                                                setSearch("");
                                                setShowDropdown(false);
                                            }}
                                            className={clsx(
                                                "tw-p-3 tw-cursor-pointer hover:tw-bg-gray-100 tw-border-b tw-border-gray-200 last:tw-border-b-0 tw-flex tw-items-center tw-justify-between",
                                                isSelected && "tw-bg-blue-50",
                                            )}
                                        >
                                            <div className="tw-flex-1">
                                                <div className="tw-font-medium tw-text-gray-900">
                                                    {agent.firstName} {agent.lastName}
                                                </div>
                                                <div className="tw-text-sm tw-text-gray-600">
                                                    {agent.email} {agent.mlsId && `• MLS: ${agent.mlsId}`}
                                                </div>
                                            </div>
                                            {isSelected && (
                                                <svg
                                                    className="tw-w-5 tw-h-5 tw-text-blue-600 tw-flex-shrink-0"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            onToggleNewAgent();
                            setShowDropdown(false);
                        }}
                        className="tw-sticky tw-bottom-0 tw-w-full tw-p-3 tw-bg-blue-600 tw-text-white tw-font-medium hover:tw-bg-blue-700 tw-border-t tw-border-gray-300 tw-rounded-b-md"
                    >
                        + Add New Agent
                    </button>
                </div>
            )}
        </div>
    );
};

// Creator Typeahead Component
interface CreatorTypeaheadProps {
    value: string;
    /** Currently selected creator, controlled by the parent; null means the PriceMe Admin default */
    selected: Creator | null;
    onChange: (creatorId: string, creatorData: Creator | null) => void;
}

const CreatorTypeahead: React.FC<CreatorTypeaheadProps> = ({ value, selected, onChange }) => {
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [creators, setCreators] = useState<Creator[]>([]);
    const [loading, setLoading] = useState(false);
    const selectedItemRef = useCallback((node: HTMLDivElement | null) => {
        if (node) {
            node.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
    }, []);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        const fetchCreators = async () => {
            if (debouncedSearch.length < 2) return;

            setLoading(true);
            try {
                const data = await getUserAgents({ search: debouncedSearch, limit: 50 });
                setCreators(data || []);
            } catch (error) {
                console.error("Failed to fetch creators:", error);
                setCreators([]);
            } finally {
                setLoading(false);
            }
        };
        fetchCreators();
    }, [debouncedSearch]);

    return (
        <div className="tw-relative">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Select Creator</label>
            <input
                type="text"
                value={search}
                onChange={(e) => {
                    setSearch(e.target.value);
                    setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() =>
                    setTimeout(() => {
                        setShowDropdown(false);
                        setSearch("");
                    }, 200)
                }
                placeholder={selected ? creatorLabel(selected) : "Search creators..."}
                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
            />
            {showDropdown && (
                <div className="tw-absolute tw-z-50 tw-w-full tw-mt-1 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md tw-shadow-lg tw-max-h-60 tw-overflow-y-auto">
                    {loading ? (
                        <div className="tw-p-3 tw-text-center tw-text-gray-500">Loading...</div>
                    ) : search.length === 0 ? (
                        <div className="tw-p-3 tw-text-center tw-text-gray-500">Type to search...</div>
                    ) : creators.length === 0 ? (
                        <div className="tw-p-3 tw-text-center tw-text-gray-500">No creators found</div>
                    ) : (
                        creators.map((creator) => {
                            const isSelected = creator.id === value;
                            return (
                                <div
                                    key={creator.id}
                                    ref={isSelected ? selectedItemRef : null}
                                    onClick={() => {
                                        onChange(creator.id, creator);
                                        setSearch("");
                                        setShowDropdown(false);
                                    }}
                                    className={clsx(
                                        "tw-p-3 tw-cursor-pointer hover:tw-bg-gray-100 tw-border-b tw-border-gray-200 last:tw-border-b-0 tw-flex tw-items-center tw-justify-between",
                                        isSelected && "tw-bg-blue-50",
                                    )}
                                >
                                    <div className="tw-flex-1">
                                        <div className="tw-font-medium tw-text-gray-900">{creatorLabel(creator)}</div>
                                        <div className="tw-text-sm tw-text-gray-600">{creatorSubLabel(creator)}</div>
                                    </div>
                                    {isSelected && (
                                        <svg
                                            className="tw-w-5 tw-h-5 tw-text-blue-600 tw-flex-shrink-0"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            )}
            {selected ? (
                <div className="tw-mt-3 tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border">
                    <div className="tw-flex tw-space-x-3">
                        <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-gray-200 tw-flex tw-items-center tw-justify-center">
                            <svg className="tw-w-6 tw-h-6 tw-text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className="tw-flex-1">
                            <div className="tw-font-medium tw-text-gray-900 tw-break-words">
                                {creatorLabel(selected)}
                            </div>
                            <div className="tw-text-sm tw-text-gray-600 tw-break-words tw-inline">
                                {creatorSubLabel(selected)}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => onChange("", null)}
                            className="tw-self-center tw-p-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded tw-text-red-500 hover:tw-bg-red-500 hover:tw-text-white tw-transition-colors"
                            title="Reset to PriceMe Admin"
                        >
                            <svg className="tw-w-7 tw-h-7" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            ) : (
                <div className="tw-mt-3 tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border tw-text-sm tw-text-gray-500">
                    PriceMe Admin (default)
                </div>
            )}
        </div>
    );
};

// SortableItem component for drag and drop
interface SortableItemProps {
    item: ImageListItem;
    index: number;
    imageItems: ImageListItem[];
    onRemove: (index: number) => void;
    onMove: (fromIndex: number, toIndex: number) => void;
    propertyId?: string;
    deleteImageMutation: any;
}

const SortableItem = React.memo(function SortableItem({
    item,
    index,
    imageItems,
    onRemove,
    onMove,
    propertyId,
    deleteImageMutation,
}: SortableItemProps) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
        id: item.id,
        transition: null, // Disable transition for better performance
    });

    const style = useMemo(
        () => ({
            transform: CSS.Transform.toString(transform),
            willChange: isDragging ? "transform" : "auto",
        }),
        [transform, isDragging],
    );

    const [objectUrl, setObjectUrl] = useState<string | null>(null);

    useEffect(() => {
        let url: string | null = null;
        if (item.type === "new") {
            url = URL.createObjectURL(item.file);
            setObjectUrl(url);
        } else {
            setObjectUrl(null);
        }
        return () => {
            if (url) {
                URL.revokeObjectURL(url);
            }
        };
    }, [item]);

    const imageSrc = item.type === "existing" ? item.url : objectUrl || "";

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={clsx(
                "tw-cursor-move tw-rounded tw-overflow-hidden tw-border tw-border-gray-200 tw-shadow-md tw-group",
                isDragging && "tw-opacity-50",
            )}
        >
            <div className="tw-bg-white tw-text-gray-800 tw-text-xs tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-0">
                <input
                    type="text"
                    defaultValue={index + 1}
                    key={`position-${index}`}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();
                            const target = e.target as HTMLInputElement;
                            const newIndex = parseInt(target.value, 10) - 1;
                            if (
                                !isNaN(newIndex) &&
                                newIndex >= 0 &&
                                newIndex < imageItems.length &&
                                newIndex !== index
                            ) {
                                onMove(index, newIndex);
                            }
                            target.blur();
                        }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className={clsx(
                        "tw-bg-transparent tw-text-gray-800 tw-text-xs tw-border-none tw-outline-none tw-text-center",
                        index + 1 > 99 && "tw-w-8",
                        index + 1 > 9 && index + 1 <= 99 && "tw-w-6",
                        index + 1 <= 9 && "tw-w-4",
                    )}
                    style={{ appearance: "textfield" }}
                />
                <span className="tw-text-gray-800">/{imageItems.length}</span>
            </div>
            <div className="tw-relative">
                <img
                    src={imageSrc}
                    alt={item.type === "existing" ? `Existing ${index + 1}` : item.file.name}
                    className="tw-w-full tw-h-36 tw-object-cover"
                    onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src =
                            "data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100' height='100' fill='%23f3f4f6'/%3e%3c/svg%3e";
                    }}
                    draggable={false}
                />
                {index > 0 && (
                    <button
                        type="button"
                        onClick={() => onMove(index, index - 1)}
                        className="tw-absolute tw-left-1 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-bg-black tw-bg-opacity-50 tw-text-white tw-rounded-full tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center tw-opacity-0 group-hover:tw-opacity-100"
                    >
                        ‹
                    </button>
                )}
                {index < imageItems.length - 1 && (
                    <button
                        type="button"
                        onClick={() => onMove(index, index + 1)}
                        className="tw-absolute tw-right-1 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-bg-black tw-bg-opacity-50 tw-text-white tw-rounded-full tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center tw-opacity-0 group-hover:tw-opacity-100"
                    >
                        ›
                    </button>
                )}
            </div>
            <div className="tw-bg-white tw-text-gray-800 tw-text-xs tw-p-2 tw-flex tw-items-center tw-justify-between">
                <div className="tw-truncate tw-flex-1">
                    {item.type === "new" ? item.file.name : `Image ${index + 1}`}
                </div>
                <button
                    type="button"
                    onClick={() => {
                        if (item.type === "existing" && propertyId) {
                            deleteImageMutation.mutate({ propertyId, imageIndex: index });
                        } else {
                            onRemove(index);
                        }
                    }}
                    className="tw-ml-2 tw-flex-shrink-0 tw-rounded-full tw-p-2 tw-bg-white tw-border tw-border-gray-400"
                >
                    <svg className="tw-w-4 tw-h-4" fill="#000" viewBox="0 0 20 20">
                        <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                            clipRule="evenodd"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );
});

export default function AdminPropertyEditor(): React.ReactElement {
    const navigate = useNavigate();
    const params = useParams();
    const isAddMode = useMemo(() => !params.slug || params.slug === "add", [params.slug]);

    const [form, setForm] = useState<Record<string, string>>({});
    const [originalForm, setOriginalForm] = useState<Record<string, string>>({});
    // Creator object lives outside `form` (which only holds strings); `form.selectedCreatorId` carries the id
    const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
    const originalCreatorRef = useRef<Creator | null>(null);
    const [pristine, setPristine] = useState(true);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [playablePropertyId, setPlayablePropertyId] = useState<string | null>(null);
    const [longLinkEdited, setLongLinkEdited] = useState(false);
    /** Single ordered list: existing URLs + new files. Sent as propertyData.imageItems + multipart "images" (new only). */
    const [imageItems, setImageItems] = useState<ImageListItem[]>([]);
    const [dragActive, setDragActive] = useState(false);
    const [showImagesModal, setShowImagesModal] = useState(false);
    const [showClearAllModal, setShowClearAllModal] = useState(false);
    const [agentImage, setAgentImage] = useState<File | null>(null);
    const [showAgentFields, setShowAgentFields] = useState(false);
    const isUploadingAgentImage = false;
    const initialImageItemsRef = React.useRef<ImageListItem[]>([]);
    // Time-related states for time picker
    const [playTime, setPlayTime] = useState({ hour: 12, minute: 0, ampm: "AM" });
    const [showHourDropdown, setShowHourDropdown] = useState(false);
    const [showMinuteDropdown, setShowMinuteDropdown] = useState(false);
    const queryClient = useQueryClient();
    const { hasAdminAccess, isLoading: adminAccessLoading } = useAdminAccess();

    // Memoize agent image preview URL and revoke on cleanup
    const agentImagePreviewUrl = useMemo(() => {
        return agentImage ? URL.createObjectURL(agentImage) : null;
    }, [agentImage]);

    useEffect(() => {
        return () => {
            if (agentImagePreviewUrl) {
                URL.revokeObjectURL(agentImagePreviewUrl);
            }
        };
    }, [agentImagePreviewUrl]);

    // Setup sensors for drag and drop with optimized settings
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 3,
            },
        }),
    );

    // Memoize image items IDs to prevent recalculation
    const imageItemIds = useMemo(() => imageItems.map((img) => img.id), [imageItems]);

    // Collapsible sections state - all expanded by default
    const [expandedSections, setExpandedSections] = useState({
        property: true,
        puzzle: true,
        agent: true,
    });

    // Clear image list when in add mode
    useEffect(() => {
        if (isAddMode) setImageItems([]);
    }, [isAddMode]);

    // Close hour/minute dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            if (!target.closest(".hour-dropdown-container")) {
                setShowHourDropdown(false);
            }
            if (!target.closest(".minute-dropdown-container")) {
                setShowMinuteDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetch property data in edit mode
    const {
        data: propertyData,
        isLoading: isLoadingProperty,
        isError: isPropertyLoadError,
    } = useQuery({
        queryKey: ["playable-property", params.slug],
        queryFn: () => getPlayablePropertyBySlug(params.slug!),
        enabled: !isAddMode && !!params.slug,
    });

    // Fetch sales data (includes agent info) separately since playable property doesn't include it
    const propertyId = propertyData?.data?.property?.id || propertyData?.data?.propertyId;
    const { data: salesData, isLoading: isLoadingSalesData } = useQuery({
        queryKey: ["property-sales-data", propertyId],
        queryFn: () => getSalesData(propertyId!),
        enabled: !isAddMode && !!propertyId,
    });

    const handleChange = useCallback((key: string, value: string) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        if (key === "longLink") {
            setLongLinkEdited(true);
        }
        setErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
        setPristine(false);
    }, []);

    const handleReset = () => {
        if (isAddMode) {
            setForm({});
            setSelectedCreator(null);
            setPlayTime({ hour: 12, minute: 0, ampm: "AM" }); // Reset time
        } else {
            setForm({ ...originalForm });
            setSelectedCreator(originalCreatorRef.current);
            // Parse time from original form
            if (originalForm.playDate) {
                const parsed = parseDateTime(originalForm.playDate);
                setPlayTime({ hour: parsed.hour, minute: parsed.minute, ampm: parsed.ampm });
            } else {
                setPlayTime({ hour: 12, minute: 0, ampm: "AM" });
            }
        }
        setImageItems(isAddMode ? [] : [...initialImageItemsRef.current]);
        setAgentImage(null);
        setPristine(true);
        setErrors({});
        setLongLinkEdited(false);
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = Array.from(e.dataTransfer.files).filter((file) => file.type.startsWith("image/"));

        if (files.length > 0) {
            setImageItems((prev) => [
                ...prev,
                ...files.map((file) => ({ type: "new" as const, file, id: `new-${Date.now()}-${Math.random()}` })),
            ]);
            setPristine(false);
        }
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []).filter((file) => file.type.startsWith("image/"));

        if (files.length > 0) {
            setImageItems((prev) => [
                ...prev,
                ...files.map((file) => ({ type: "new" as const, file, id: `new-${Date.now()}-${Math.random()}` })),
            ]);
            setPristine(false);
        }
        e.target.value = "";
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setImageItems((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);

                if (oldIndex === -1 || newIndex === -1) return items;

                const newItems = [...items];
                const [movedItem] = newItems.splice(oldIndex, 1);
                newItems.splice(newIndex, 0, movedItem);
                return newItems;
            });
            setPristine(false);
        }
    };

    const removeImageItem = (index: number) => {
        setImageItems((prev) => prev.filter((_, i) => i !== index));
        setPristine(false);
    };

    const moveImageItem = (fromIndex: number, toIndex: number) => {
        setImageItems((prev) => {
            const next = [...prev];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            return next;
        });
        setPristine(false);
    };

    const toggleSection = (section: keyof typeof expandedSections) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };
    const updatePropertyMutation = useMutation({
        mutationFn: ({
            id,
            payload,
            images,
            agentImage,
        }: {
            id: string;
            payload: UpdatePlayablePropertyPayload;
            images?: File[];
            agentImage?: File;
        }) => updatePlayableProperty(id, payload, images, agentImage),
        onSuccess: async () => {
            toast.success("Property updated successfully");
            setImageItems([]);
            setAgentImage(null);
            // Refetch the property data to get updated values
            await queryClient.invalidateQueries({
                queryKey: ["playable-property", params.slug],
            });
            await queryClient.invalidateQueries({
                queryKey: ["admin-playable-properties"],
            });
            // Update originalForm to current form state so pristine is calculated correctly
            setOriginalForm({ ...form });
            setPristine(true);
        },
        onError: (error: any) => {
            const msg = error.response?.data?.message;
            const message = Array.isArray(msg) ? msg.join(" ") : msg;
            toast.error(message || "Failed to update property");
        },
    });

    // Mutation for creating playable property (payload + optional images for multipart upload)
    const createPropertyMutation = useMutation({
        mutationFn: (args: { payload: any; images?: File[]; agentImage?: File }) =>
            setDailyPlayableProperty(args.payload, args.images, args.agentImage),
        onSuccess: async () => {
            toast.success("Property created successfully");
            setPristine(true);
            // Navigate back to admin page
            navigate("/admin/set-puzzle");
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || "Failed to create property");
        },
    });

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        const requiredFields = [
            ...propertyFields.filter((f) => f.required).map((f) => f.key),
            ...puzzleFields.filter((f) => f.required).map((f) => f.key),
        ];

        requiredFields.forEach((fieldKey) => {
            // uploadedImages is state (File[]), not a form field — validated below
            if (fieldKey === "uploadedImages") return;

            const value = form[fieldKey];
            if (!value || value.trim().length === 0) {
                const friendlyName = fieldLabelMap[fieldKey] || "Field";
                newErrors[fieldKey] = `${friendlyName} required`;
            }
        });

        // Image upload validation
        if (isAddMode && imageItems.length < 5) {
            newErrors.uploadedImages = "Minimum 5 images required";
        }

        // Conditional listingId validation - required only when both url and propertyData are absent
        const hasUrl = normalizeValue(form.longLink);
        const hasPropertyData = Object.keys({
            streetAddress: normalizeValue(form.streetAddress),
            city: normalizeValue(form.city),
            state: normalizeValue(form.state),
            zipCode: normalizeValue(form.zipCode),
            listPrice: stripNumericFormatting(form.listPrice),
            lat: stripNumericFormatting(form.lat),
            lng: stripNumericFormatting(form.lng),
        }).some((key) => {
            const value =
                key === "listPrice" || key === "lat" || key === "lng"
                    ? stripNumericFormatting(form[key])
                    : normalizeValue(form[key]);
            return value !== undefined;
        });

        if (!hasUrl && !hasPropertyData && !normalizeValue(form.listingId)) {
            newErrors.listingId = "Listing ID required when URL and property data are not provided";
        }

        // Numeric validation
        numericFieldKeys.forEach((fieldKey) => {
            const value = form[fieldKey];
            if (value && value.trim().length > 0) {
                const cleanedValue = stripNumericFormatting(value);
                if (!cleanedValue) {
                    newErrors[fieldKey] = "Must be a number";
                    return;
                }

                const numericValue = Number(cleanedValue);
                if (Number.isNaN(numericValue)) {
                    newErrors[fieldKey] = "Must be a number";
                }
            }
        });

        // URL validation
        urlFieldKeys.forEach((fieldKey) => {
            const value = form[fieldKey];
            if (value && value.trim().length > 0) {
                try {
                    const candidate = value.trim();
                    // Allow relative puzzle links (starting with /)
                    if (!candidate.startsWith("/")) {
                        new URL(candidate);
                    }
                } catch {
                    newErrors[fieldKey] = "Invalid URL";
                }
            }
        });

        // Email validation
        emailFieldKeys.forEach((fieldKey) => {
            const value = form[fieldKey];
            if (value && value.trim().length > 0) {
                const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailPattern.test(value.trim())) {
                    newErrors[fieldKey] = "Invalid email";
                }
            }
        });

        // Date & Time validation (supports ISO datetime, YYYY-MM-DD HH:mm:ss, or YYYY-MM-DD)
        if (form.playDate && form.playDate.trim().length > 0) {
            const dateStr = form.playDate.trim();
            // Check if it's an ISO datetime string (YYYY-MM-DDTHH:mm:ss or YYYY-MM-DDTHH:mm)
            if (dateStr.includes("T")) {
                const date = new Date(dateStr);
                if (isNaN(date.getTime())) {
                    newErrors.playDate = "Invalid date and time";
                }
            } else if (dateStr.includes(" ") && dateStr.split(" ")[1]?.includes(":")) {
                // Format: YYYY-MM-DD HH:mm:ss
                const date = new Date(dateStr.replace(" ", "T"));
                if (isNaN(date.getTime())) {
                    newErrors.playDate = "Invalid date and time";
                }
            } else if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                newErrors.playDate = "Use YYYY-MM-DD or YYYY-MM-DD HH:mm:ss format";
            } else if (!parseDateFromYMD(dateStr)) {
                newErrors.playDate = "Invalid date";
            }
        }

        return newErrors;
    };

    const getInputClasses = (fieldKey: string, extra?: string) =>
        [
            "tw-w-full",
            "tw-px-3",
            "tw-py-2",
            "tw-border",
            "tw-rounded-md",
            "focus:tw-outline-none",
            errors[fieldKey]
                ? "tw-border-red-500 focus:tw-ring-2 focus:tw-ring-red-500"
                : "tw-border-gray-300 focus:tw-ring-2 focus:tw-ring-blue-500",
            extra,
        ]
            .filter(Boolean)
            .join(" ");

    const normalizeValue = (value?: string) => {
        if (!value) return undefined;
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : undefined;
    };

    const parseOptionalBooleanString = (value?: string): boolean | undefined => {
        if (value === "true") return true;
        if (value === "false") return false;
        return undefined;
    };

    const getAgentAccountActionFlags = () => ({
        sendClaimAccountLink: parseOptionalBooleanString(form.sendClaimAccountLink),
        sendPasswordReset: parseOptionalBooleanString(form.sendPasswordReset),
    });

    const normalizePostBody = (value?: string) => (normalizeValue(value) ? value : null);

    const selectedAgentHasAuth0UserId = Boolean(normalizeValue(form.selectedAgentAuth0UserId));
    const isSendClaimAccountLinkDisabled =
        !normalizeValue(form.selectedAgentId) || selectedAgentHasAuth0UserId || form.sendPasswordReset === "true";
    const isSendPasswordResetDisabled =
        !normalizeValue(form.selectedAgentId) || !selectedAgentHasAuth0UserId || form.sendClaimAccountLink === "true";

    const stripNumericFormatting = (value?: string) => {
        if (!value) return undefined;
        const trimmed = value.trim();
        if (!trimmed) return undefined;
        return trimmed.replace(/,/g, "");
    };

    const convertPropertyType = (value?: string): string | undefined => {
        if (!value) return undefined;
        const normalized = value.trim().toLowerCase();

        // Property type mappings
        const typeMap: Record<string, string> = {
            condo: "Condominium",
            condominium: "Condominium",
            condos: "Condominium",
            house: "Single Family Residential",
            "single family": "Single Family Residential",
            "single-family": "Single Family Residential",
            sfr: "Single Family Residential",
        };

        // Check for exact match first
        if (typeMap[normalized]) {
            return typeMap[normalized];
        }

        // Check for partial matches
        for (const [key, mappedValue] of Object.entries(typeMap)) {
            if (normalized.includes(key) || key.includes(normalized)) {
                return mappedValue;
            }
        }

        // If no match found, capitalize first letter of each word
        return value
            .split(/\s+/)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    };

    const formatDateToYMD = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const formatDateToYYMMDD = (date: Date) => {
        const year = String(date.getFullYear()).slice(-2);
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}${month}${day}`;
    };

    const parseDateFromYMD = (value?: string) => {
        if (!value) return null;
        const [yearStr, monthStr, dayStr] = value.split("-");
        const year = Number(yearStr);
        const month = Number(monthStr);
        const day = Number(dayStr);

        if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day) || month < 1 || month > 12) {
            return null;
        }

        // Get max days in the month
        const maxDay = new Date(year, month, 0).getDate();
        if (day < 1 || day > maxDay) {
            return null;
        }

        const parsed = new Date(year, month - 1, day);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    // Convert date and time to datetime string in format: YYYY-MM-DD HH:mm:ss (CST)
    const combineDateAndTime = useCallback((date: Date | null, hour: number, minute: number, ampm: string): string => {
        if (!date) return "";
        // Parse date in CST timezone
        const d = moment.tz(date, "America/Chicago");
        let h = hour;
        if (ampm === "AM" && hour === 12) h = 0;
        if (ampm === "PM" && hour !== 12) h += 12;
        d.set({ hour: h, minute, second: 0, millisecond: 0 });

        // Format as YYYY-MM-DD HH:mm:ss in CST
        return d.format("YYYY-MM-DD HH:mm:ss");
    }, []);

    // Parse datetime string to extract date and time (supports both ISO and YYYY-MM-DD HH:mm:ss formats) in CST
    const parseDateTime = useCallback(
        (value?: string): { date: Date | null; hour: number; minute: number; ampm: string } => {
            if (!value) return { date: null, hour: 12, minute: 0, ampm: "AM" };

            let m: moment.Moment;
            if (value.includes("T")) {
                // ISO format: YYYY-MM-DDTHH:mm:ss.sssZ - parse as UTC and convert to CST
                m = moment(value).tz("America/Chicago");
            } else if (value.includes(" ") && value.split(" ")[1]?.includes(":")) {
                // Format: YYYY-MM-DD HH:mm:ss - interpret as CST
                m = moment.tz(value, "YYYY-MM-DD HH:mm:ss", "America/Chicago");
            } else {
                // Date-only string - interpret as CST with default time
                const parsed = parseDateFromYMD(value);
                if (!parsed) {
                    return { date: null, hour: 12, minute: 0, ampm: "AM" };
                }
                m = moment.tz(parsed, "America/Chicago");
            }

            if (!m.isValid()) {
                return { date: null, hour: 12, minute: 0, ampm: "AM" };
            }

            const date = m.toDate();
            let hour = m.hour();
            const minute = m.minute();
            const ampm = hour >= 12 ? "PM" : "AM";
            if (hour === 0) hour = 12;
            else if (hour > 12) hour -= 12;

            return { date, hour, minute, ampm };
        },
        [],
    );

    // Update playDate when time changes
    useEffect(() => {
        if (!form.playDate) return;

        const parsed = parseDateTime(form.playDate);
        if (parsed.date) {
            const dateTimeStr = combineDateAndTime(parsed.date, playTime.hour, playTime.minute, playTime.ampm);
            if (dateTimeStr !== form.playDate) {
                handleChange("playDate", dateTimeStr);
            }
        }
    }, [playTime.hour, playTime.minute, playTime.ampm, form.playDate, parseDateTime, combineDateAndTime, handleChange]);

    useEffect(() => {
        if (isSendClaimAccountLinkDisabled && form.sendClaimAccountLink === "true") {
            handleChange("sendClaimAccountLink", "false");
        }
        if (isSendPasswordResetDisabled && form.sendPasswordReset === "true") {
            handleChange("sendPasswordReset", "false");
        }
    }, [
        form.sendClaimAccountLink,
        form.sendPasswordReset,
        isSendClaimAccountLinkDisabled,
        isSendPasswordResetDisabled,
        handleChange,
    ]);

    // Auto-generate Long link when address + play date are complete (add mode only, and while not manually edited)
    useEffect(() => {
        if (!isAddMode || longLinkEdited) return;

        const streetAddress = form.streetAddress?.trim();
        const city = form.city?.trim();
        const state = form.state?.trim();
        const zipCode = form.zipCode?.trim();
        const playDateStr = form.playDate?.trim();

        if (!streetAddress || !city || !state || !zipCode || !playDateStr) {
            return;
        }

        // Extract date from datetime string (supports both "T" and space separators)
        let dateOnlyStr = playDateStr;
        if (playDateStr.includes("T")) {
            dateOnlyStr = playDateStr.split("T")[0];
        } else if (playDateStr.includes(" ")) {
            dateOnlyStr = playDateStr.split(" ")[0];
        }

        const playDate = parseDateFromYMD(dateOnlyStr);
        if (!playDate) return;

        const linkDate = formatDateToYYMMDD(playDate);
        const fullAddress = `${streetAddress} ${city} ${state} ${zipCode}`.trim();
        // Must match slugifyAddress in common/utils/puzzle-link.utils.ts (the server normalizes on save anyway)
        const slug = fullAddress
            .toLowerCase()
            // Remove punctuation/special chars, keep letters, numbers, spaces and hyphens
            .replace(/[^a-z0-9\s-]/g, "")
            .replace(/\s+/g, "-")
            // Collapse repeated hyphens and trim hyphens from both ends
            .replace(/-+/g, "-")
            .replace(/^-+|-+$/g, "");

        const autoLongLink = `/${linkDate}/${slug}`;

        if (form.longLink !== autoLongLink) {
            setForm((prev) => ({
                ...prev,
                longLink: autoLongLink,
            }));
        }
    }, [
        form.streetAddress,
        form.city,
        form.state,
        form.zipCode,
        form.playDate,
        form.longLink,
        isAddMode,
        longLinkEdited,
    ]);

    const handleSubmit = () => {
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error("Please fix the highlighted fields");
            return;
        }
        setErrors({});
        const normalizedMlsId = normalizeMlsId(form.listingId);
        const normalizedImageIndex = normalizeValue(form.imageIndex);
        const normalizedEmailImage = normalizeValue(form.emailImage);
        const parsedImageIndex = normalizedImageIndex ? parseInt(normalizedImageIndex, 10) : undefined;
        const parsedEmailImage = normalizedEmailImage ? parseInt(normalizedEmailImage, 10) : undefined;
        const sendDailyEmail = form.sendDailyEmail === "true";

        // Transform form data to API format
        // When a new agent image is selected, send empty string for agentImageLink to clear the old URL
        const listingAgent = {
            agentFirstName: normalizeValue(form.agentFirstName),
            agentLastName: normalizeValue(form.agentLastName),
            agentEmail: normalizeValue(form.agentEmail),
            agentPhoneNumber: normalizeValue(form.agentPhoneNumber),
            agentBrokerage: normalizeValue(form.agentBrokerage),
            agentPrimaryLink: normalizeValue(form.agentPrimaryLink),
            agentImageLink: agentImage ? "" : normalizeValue(form.agentImageLink),
            agentInstagram: normalizeValue(form.agentInstagram),
            agentTiktok: normalizeValue(form.agentTiktok),
            agentTwitter: normalizeValue(form.agentTwitter),
            agentFacebook: normalizeValue(form.agentFacebook),
            agentYoutube: normalizeValue(form.agentYoutube),
            agentMlsId: normalizeValue(form.agentMlsId),
        };

        // Only include "new" in API payload when we have a valid File (keeps imageItems "new" count in sync with uploaded files)
        const imageItemsApi: ImageItemApi[] | undefined =
            imageItems.length > 0
                ? imageItems
                      .map((item) => {
                          if (item.type === "existing") return { type: "existing" as const, url: item.url };
                          if (item.type === "new" && item.file) return { type: "new" as const };
                          return null;
                      })
                      .filter((x): x is ImageItemApi => x != null)
                : undefined;
        const newImageFiles = imageItems
            .filter((i): i is { type: "new"; file: File; id: string } => i.type === "new" && !!i.file)
            .map((i) => i.file);

        if (imageItems.some((i) => i.type === "new" && "file" in i && !i.file)) {
            toast.error("Some uploaded images are missing. Please re-add them and save again.");
            return;
        }

        const propertyData = {
            streetAddress: normalizeValue(form.streetAddress),
            city: normalizeValue(form.city),
            state: normalizeValue(form.state),
            zipCode: normalizeValue(form.zipCode),
            neighborhood: normalizeValue(form.neighborhood) || undefined,
            publicRemarks: normalizeValue(form.publicRemarks),
            listPrice: stripNumericFormatting(form.listPrice),
            bedrooms: stripNumericFormatting(form.bedrooms),
            bathrooms: stripNumericFormatting(form.bathrooms),
            propertyType: convertPropertyType(form.propertyType),
            squareFootage: stripNumericFormatting(form.squareFootage),
            parkingSpots: stripNumericFormatting(form.parkingSpots),
            parkingType: normalizeValue(form.parkingType),
            yearBuilt: stripNumericFormatting(form.yearBuilt),
            exteriorFeatures: normalizeValue(form.exteriorFeatures),
            interiorFeatures: normalizeValue(form.interiorFeatures),
            heating: normalizeValue(form.heating),
            cooling: normalizeValue(form.cooling),
            laundryFeatures: normalizeValue(form.laundryFeatures),
            lotDimensions: normalizeValue(form.lotDimensions),
            listingId: normalizeValue(form.listingId) || normalizedMlsId || undefined,
            externalPropertyLink: normalizeValue(form.externalPropertyLink),
            lat: stripNumericFormatting(form.lat),
            lng: stripNumericFormatting(form.lng),
            ...(imageItemsApi?.length ? { imageItems: imageItemsApi } : {}),
        };

        // Normalize play date for API
        const normalizedPlayDate = normalizeValue(form.playDate);
        let apiPlayDate = normalizedPlayDate;

        // If Send Daily Email is enabled, ensure playDate has midnight time (00:00:00).
        // We normalize by stripping any time/ISO portion so the backend treats it as date-only (midnight).
        if (sendDailyEmail && apiPlayDate) {
            const trimmed = apiPlayDate.trim();
            // Handle ISO (YYYY-MM-DDTHH:mm:ss) or "YYYY-MM-DD HH:mm:ss"
            const dateOnly = trimmed.includes("T")
                ? trimmed.split("T")[0]
                : trimmed.includes(" ")
                  ? trimmed.split(" ")[0]
                  : trimmed;
            apiPlayDate = dateOnly;
        }

        if (isAddMode) {
            // Create new property (pass uploadedImages for multipart/form-data when present)
            createPropertyMutation.mutate({
                payload: {
                    playDate: apiPlayDate,
                    url: normalizeValue(form.longLink),
                    mlsId: normalizedMlsId,
                    image_index: parsedImageIndex ?? 1,
                    listingAgent: Object.keys(listingAgent).some(
                        (key) => listingAgent[key as keyof typeof listingAgent],
                    )
                        ? listingAgent
                        : undefined,
                    propertyData: Object.keys(propertyData).some(
                        (key) => propertyData[key as keyof typeof propertyData],
                    )
                        ? propertyData
                        : undefined,
                    emailPuzzleSubject: normalizeValue(form.emailSubject),
                    emailPuzzleImage: parsedEmailImage,
                    emailPuzzleBody: normalizeValue(form.emailBody),
                    postBody: normalizePostBody(form.postBody),
                    featureAgent: parseOptionalBooleanString(form.featureAgent),
                    longLink: normalizeValue(form.longLink),
                    sendDailyEmail,
                    ...getAgentAccountActionFlags(),
                    creatorUserId: normalizeValue(form.selectedCreatorId),
                },
                images: newImageFiles.length > 0 ? newImageFiles : undefined,
                agentImage: agentImage || undefined,
            } as any);
        } else {
            // Update existing property
            if (!playablePropertyId) {
                toast.error("Property ID not found");
                return;
            }

            updatePropertyMutation.mutate({
                id: playablePropertyId,
                payload: {
                    playDate: apiPlayDate,
                    url: normalizeValue(form.longLink),
                    longLink: normalizeValue(form.longLink),
                    mlsId: normalizeValue(form.listingId),
                    imageIndex: parsedImageIndex ?? 1,
                    listingAgent: Object.keys(listingAgent).some(
                        (key) => listingAgent[key as keyof typeof listingAgent],
                    )
                        ? listingAgent
                        : undefined,
                    propertyData: Object.keys(propertyData).some(
                        (key) => propertyData[key as keyof typeof propertyData],
                    )
                        ? propertyData
                        : undefined,
                    emailPuzzleSubject: normalizeValue(form.emailSubject),
                    ...(parsedEmailImage !== undefined && { emailPuzzleImage: parsedEmailImage }),
                    emailPuzzleBody: normalizeValue(form.emailBody),
                    postBody: normalizePostBody(form.postBody),
                    featureAgent: parseOptionalBooleanString(form.featureAgent),
                    sendDailyEmail,
                    ...getAgentAccountActionFlags(),
                    // A cleared creator is sent as null so the server resets it to the PriceMe Admin user
                    creatorUserId:
                        normalizeValue(form.selectedCreatorId) ?? (originalForm.selectedCreatorId ? null : undefined),
                },
                images: newImageFiles.length > 0 ? newImageFiles : undefined,
                agentImage: agentImage || undefined,
            });
        }
    };

    // Mutation for deleting playable property
    const deletePropertyMutation = useMutation({
        mutationFn: (id: string) => deletePlayableProperty(id),
        onSuccess: async (_, deletedId: string) => {
            setShowRemoveModal(false);
            toast.success("Playable property removed successfully");

            // Invalidate all admin-playable-properties queries to trigger background refetch
            queryClient.invalidateQueries({
                queryKey: ["admin-playable-properties"],
                refetchType: "active", // Force refetch for active queries
            });

            // Navigate back to admin page (query will refetch on mount due to refetchOnMount: true)
            setTimeout(() => {
                window.location.href = "/admin/set-puzzle";
            }, 1000);
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || "Failed to remove playable property");
        },
    });

    // Mutation for deleting property image
    const deleteImageMutation = useMutation({
        mutationFn: ({ propertyId, imageIndex }: { propertyId: string; imageIndex: number }) =>
            deletePropertyImage(propertyId, imageIndex),
        onSuccess: async (_, { imageIndex }) => {
            toast.success("Image deleted successfully");
            // Remove the image from local state
            setImageItems((prev) => prev.filter((_, index) => index !== imageIndex));
            setPristine(false);
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || "Failed to delete image");
        },
    });

    // Track pristine state by comparing form to originalForm and agentImage state
    useEffect(() => {
        if (isAddMode) {
            // In add mode, pristine means form is empty and no agent image
            const isEmpty = Object.values(form).every((val) => !val || val === "") && !agentImage;
            if (isEmpty !== pristine) {
                setPristine(isEmpty);
            }
        } else if (Object.keys(originalForm).length > 0) {
            // In edit mode, pristine means form matches original AND no new agent image
            const isFormPristine = JSON.stringify(form) === JSON.stringify(originalForm) && !agentImage;
            if (isFormPristine !== pristine) {
                setPristine(isFormPristine);
            }
        }
    }, [form, originalForm, pristine, isAddMode, agentImage]);

    // Fetch next available play date in add mode (only once on mount)
    useEffect(() => {
        if (isAddMode && !form.playDate) {
            const findNextAvailableDate = async () => {
                try {
                    // Use CST timezone for today
                    const today = moment.tz("America/Chicago").startOf("day").toDate();

                    // Fetch all playable properties for the next 365 days to find gaps
                    const endDate = moment.tz("America/Chicago").startOf("day").add(365, "days").toDate();
                    const startDateStr = formatDateToYMD(today);
                    const endDateStr = formatDateToYMD(endDate);

                    let occupiedDates = new Set<string>();
                    try {
                        const response = await getPlayableProperties({
                            minPlayDate: startDateStr,
                            maxPlayDate: endDateStr,
                        });

                        // Extract dates that have properties
                        const properties = Array.isArray(response) ? response : (response as any)?.data || [];
                        properties.forEach((prop: any) => {
                            if (prop.playDate) {
                                const dateStr =
                                    typeof prop.playDate === "string"
                                        ? prop.playDate.split("T")[0]
                                        : formatDateToYMD(new Date(prop.playDate));
                                occupiedDates.add(dateStr);
                            }
                        });
                    } catch (error) {
                        // If no properties found in range, that's fine - all dates are available
                    }

                    // Find the first date that doesn't have a property
                    for (let i = 0; i < 365; i++) {
                        const checkDate = moment.tz("America/Chicago").startOf("day").add(i, "days").toDate();
                        const dateStr = formatDateToYMD(checkDate);

                        if (!occupiedDates.has(dateStr)) {
                            // Found an available date - set with default time from playTime state
                            const dateTimeStr = combineDateAndTime(
                                checkDate,
                                playTime.hour,
                                playTime.minute,
                                playTime.ampm,
                            );
                            handleChange("playDate", dateTimeStr);
                            break;
                        }
                    }
                } catch (error) {
                    // If error, default to tomorrow with default time from playTime state
                    const tomorrow = moment.tz("America/Chicago").startOf("day").add(1, "days").toDate();
                    const dateTimeStr = combineDateAndTime(tomorrow, playTime.hour, playTime.minute, playTime.ampm);
                    handleChange("playDate", dateTimeStr);
                }
            };

            findNextAvailableDate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAddMode]);

    // Populate form when property data is loaded, and update when sales data loads
    useEffect(() => {
        if (propertyData?.data && !isAddMode) {
            const property = propertyData.data;
            const propertyInfo = property.property || {};

            // Get agent info from sales data (which includes the agent) or fallback to property data
            // getSalesData returns axios response.data, so React Query's data is the API response directly
            let agentInfo: any = {};

            // Always try to get agent from salesData first (this will be empty initially, then populated when salesData loads)
            if (salesData) {
                // Try direct access (salesData.sales_data.listingAgent)
                agentInfo = (salesData as any)?.sales_data?.listingAgent || {};

                // Fallback to nested data structure in case it's wrapped differently
                if (!agentInfo || Object.keys(agentInfo).length === 0) {
                    agentInfo = (salesData?.data as any)?.sales_data?.listingAgent || {};
                }
            }

            // Final fallback to property data (from resolve API – has listingAgent with img)
            if (!agentInfo || Object.keys(agentInfo).length === 0) {
                agentInfo = property.property?.listingAgent || property.property?.agent || {};
            }
            // Prefer agent image from resolved property when available (so edit shows uploaded image)
            const resolvedAgentImg =
                (property.property?.listingAgent as { img?: string; imageLink?: string } | undefined)?.img ||
                (property.property?.listingAgent as { img?: string; imageLink?: string } | undefined)?.imageLink;
            if (resolvedAgentImg) {
                agentInfo = { ...agentInfo, img: resolvedAgentImg, imageLink: resolvedAgentImg };
            }

            // Store the playable property ID for deletion
            if (property.id) {
                setPlayablePropertyId(property.id);
            }

            const formattedPlayDate = propertyData.data.playDate
                ? cstMoment(propertyData.data.playDate).format("YYYY-MM-DD HH:mm:ss")
                : "";

            const preservedSendClaimAccountLink = form.sendClaimAccountLink ?? "false";
            const preservedSendPasswordReset = form.sendPasswordReset ?? "false";

            const formData: Record<string, string> = {
                // Property fields
                streetAddress: propertyInfo.streetAddress || "",
                city: propertyInfo.city || "",
                state: propertyInfo.state || "",
                zipCode: propertyInfo.zipCode || "",
                neighborhood: propertyInfo.neighborhood || "",
                publicRemarks: propertyInfo.publicRemarks || "",
                listPrice: propertyInfo.listPrice?.toString() || "",
                bedrooms: propertyInfo.bedrooms?.toString() || "",
                bathrooms: propertyInfo.bathrooms?.toString() || "",
                propertyType: propertyInfo.propertyType || "",
                squareFootage: propertyInfo.squareFootage?.toString() || "",
                parkingSpots: propertyInfo.parkingSpots?.toString() || "",
                parkingType: propertyInfo.parkingType || "",
                yearBuilt: propertyInfo.yearBuilt?.toString() || "",
                exteriorFeatures: propertyInfo.exteriorFeatures || "",
                interiorFeatures: propertyInfo.interiorFeatures || "",
                heating: propertyInfo.heating || "",
                cooling: propertyInfo.cooling || "",
                laundryFeatures: propertyInfo.laundryFeatures || "",
                lotDimensions: propertyInfo.lotDimensions || "",
                listingId: property.listingId || propertyData.data.listingId || propertyInfo.listingId || "",
                externalPropertyLink: propertyInfo.externalPropertyLink || propertyInfo.external_property_link || "",
                lat: propertyInfo.lat?.toString() || "",
                lng: propertyInfo.lng?.toString() || "",
                imageUrls: propertyInfo.imageUrls?.join("\n") || "",

                // Puzzle fields
                playDate: formattedPlayDate,
                mlsId:
                    property.listingId ||
                    property.mlsId ||
                    propertyData.data.listingId ||
                    propertyData.data.mlsId ||
                    propertyInfo.mlsId ||
                    "",
                longLink: propertyData.data.longLink || "",
                imageIndex: property.imageIndex?.toString() || "",
                emailSubject: property.emailSubject || property.emailPuzzleSubject || "",
                emailImage: property.emailImage?.toString() || property.emailPuzzleImage?.toString() || "",
                emailBody: property.emailBody || property.emailPuzzleBody || "",
                postBody: property.body || property.postBody || property.post_body || "",
                featureAgent: property.featureAgent === true ? "true" : property.featureAgent === false ? "false" : "",
                // Daily email toggle
                sendDailyEmail: property.sendDailyEmail === true ? "true" : "false",

                // Agent fields
                agentFirstName: agentInfo.firstName || "",
                agentLastName: agentInfo.lastName || "",
                agentEmail: agentInfo.email || "",
                agentPhoneNumber: agentInfo.phoneNumber || "",
                agentBrokerage: agentInfo.brokerage || "",
                agentPrimaryLink: agentInfo.link || "",
                agentImageLink: agentInfo.img ?? agentInfo.imageLink ?? "",
                agentInstagram: agentInfo.instagram || "",
                agentTiktok: agentInfo.tiktok || "",
                agentTwitter: agentInfo.xTwitter || "",
                agentFacebook: agentInfo.facebook || "",
                agentYoutube: agentInfo.youtube || "",
                agentMlsId: agentInfo.mlsId || "",
                selectedAgentAuth0UserId: agentInfo.auth0UserId || agentInfo.auth0_user_id || "",
                sendClaimAccountLink: preservedSendClaimAccountLink,
                sendPasswordReset: preservedSendPasswordReset,

                // Creator (empty means the PriceMe Admin default)
                selectedCreatorId: property.createdBy || "",
            };

            setForm(formData);
            setOriginalForm({ ...formData }); // Store original data for reset
            const loadedCreator: Creator | null =
                property.creator && property.createdBy ? { id: property.createdBy, ...property.creator } : null;
            originalCreatorRef.current = loadedCreator;
            setSelectedCreator(loadedCreator);

            // Show agent fields if agent data exists
            if (agentInfo.firstName || agentInfo.lastName || agentInfo.email) {
                setShowAgentFields(true);
            }

            const initialItems: ImageListItem[] = (
                Array.isArray(propertyInfo.imageUrls) ? propertyInfo.imageUrls : []
            ).map((url, index) => ({ type: "existing", url, id: `existing-${index}-${url}` }));
            setImageItems(initialItems);
            initialImageItemsRef.current = initialItems;

            // Parse datetime and set playTime
            if (formData.playDate) {
                const parsed = parseDateTime(formData.playDate);
                setPlayTime({ hour: parsed.hour, minute: parsed.minute, ampm: parsed.ampm });
            }

            setPristine(true);
            setErrors({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyData, salesData, isAddMode, isLoadingSalesData, propertyId]);

    // Show loading state while fetching property data in edit mode
    if ((!isAddMode && isLoadingProperty) || adminAccessLoading) {
        return (
            <div className="tw-min-h-screen tw-bg-gray-50 tw-p-4">
                <div className="tw-max-w-6xl tw-mx-auto">
                    <div className="tw-flex tw-items-center tw-justify-center tw-h-96">
                        <div className="tw-text-center">
                            <MoonLoader size={40} color="#63c19f" />
                            <p className="tw-mt-4 tw-text-gray-600">Loading property data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Property not found (e.g. invalid slug or 404 from API)
    if (!isAddMode && isPropertyLoadError) {
        return (
            <div className="tw-min-h-screen tw-bg-gray-50 tw-p-4">
                <div className="tw-max-w-6xl tw-mx-auto">
                    <div className="tw-flex tw-items-center tw-justify-center tw-h-96">
                        <div className="tw-text-center tw-bg-white tw-rounded tw-shadow tw-p-8 tw-max-w-md">
                            <p className="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">Property not found</p>
                            <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                                No playable property could be loaded for this link. It may have been removed or the link
                                may be incorrect.
                            </p>
                            <button
                                type="button"
                                className="tw-text-sm tw-text-gray-600 hover:tw-text-gray-900 tw-bg-gray-100 hover:tw-bg-gray-200 tw-border tw-border-gray-200 tw-rounded tw-px-4 tw-py-2"
                                onClick={() => navigate("/admin/set-puzzle")}
                            >
                                ← Back to Set Puzzle
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Not authenticated or not admin/editor
    if (!hasAdminAccess) {
        return <AdminAccessDenied redirectPath={`/admin/property/${params.slug}`} />;
    }

    return (
        <div className="tw-min-h-screen tw-bg-gray-50 tw-p-4">
            <div className="tw-max-w-6xl tw-mx-auto">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                    <h1 className="tw-text-2xl tw-font-bold">{isAddMode ? "Add Property" : "Edit Property"}</h1>
                    <button
                        type="button"
                        className="tw-text-sm tw-text-gray-600 hover:tw-text-gray-900 tw-bg-white tw-border tw-border-gray-200 tw-rounded tw-px-3 tw-py-1"
                        onClick={() => navigate("/admin/set-puzzle")}
                        title="Back to admin"
                        aria-label="Back to admin"
                    >
                        ⟵ Back to Admin
                    </button>
                </div>

                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                    <section className="tw-bg-white tw-rounded tw-shadow tw-p-4">
                        <button
                            type="button"
                            className="tw-w-full tw-flex tw-items-center tw-justify-between tw-text-left tw-font-semibold tw-text-gray-900 tw-mb-3 tw-p-0 tw-bg-transparent tw-border-none tw-cursor-pointer hover:tw-bg-gray-50 tw-rounded tw-px-2 tw-py-1 group"
                            onClick={() => toggleSection("property")}
                        >
                            Property Info
                            <svg
                                className={`tw-w-5 tw-h-5 tw-transition-transform tw-duration-200 tw-select-none tw-text-gray-600 group-hover:tw-text-gray-800 ${expandedSections.property ? "tw-rotate-180" : ""}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections.property && (
                            <div className="tw-space-y-3">
                                {propertyFields.map((f) => (
                                    <div key={f.key}>
                                        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                                            {f.label}
                                            {f.required && <span className="tw-text-red-500">*</span>}
                                        </label>
                                        {f.key === "uploadedImages" ? (
                                            <div className="tw-flex tw-gap-2">
                                                <input
                                                    type="file"
                                                    multiple
                                                    accept="image/*"
                                                    onChange={(e) => {
                                                        if (e.target.files && e.target.files.length > 0) {
                                                            const files = Array.from(e.target.files).filter((f) =>
                                                                f.type.startsWith("image/"),
                                                            );
                                                            if (files.length > 0) {
                                                                setImageItems((prev) => [
                                                                    ...prev,
                                                                    ...files.map((file) => ({
                                                                        type: "new" as const,
                                                                        file,
                                                                        id: `new-${Date.now()}-${Math.random()}`,
                                                                    })),
                                                                ]);
                                                                setShowImagesModal(true);
                                                            }
                                                        }
                                                        e.target.value = "";
                                                    }}
                                                    className="tw-hidden"
                                                    id="main-image-upload"
                                                />
                                                <input
                                                    type="text"
                                                    value={imageItems.length > 0 ? `${imageItems.length} image(s)` : ""}
                                                    onClick={() => {
                                                        if (imageItems.length > 0) {
                                                            setShowImagesModal(true);
                                                        } else {
                                                            document.getElementById("main-image-upload")?.click();
                                                        }
                                                    }}
                                                    placeholder={
                                                        isAddMode
                                                            ? "Click to upload images"
                                                            : "Click to view or add images"
                                                    }
                                                    readOnly
                                                    className={`${getInputClasses(f.key)} tw-cursor-pointer tw-flex-1`}
                                                />
                                                {imageItems.some((i) => i.type === "new") && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowClearAllModal(true)}
                                                        className="tw-bg-red-500 hover:tw-bg-red-600 tw-text-white tw-px-3 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-transition-colors"
                                                    >
                                                        Clear New
                                                    </button>
                                                )}
                                            </div>
                                        ) : f.multiline ? (
                                            <textarea
                                                value={form[f.key] || ""}
                                                onChange={(e) => handleChange(f.key, e.target.value)}
                                                placeholder={isAddMode ? f.label : undefined}
                                                className={getInputClasses(f.key, "tw-min-h-[90px]")}
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                value={form[f.key] || ""}
                                                onChange={(e) => handleChange(f.key, e.target.value)}
                                                placeholder={isAddMode ? f.label : undefined}
                                                className={getInputClasses(f.key)}
                                            />
                                        )}
                                        {errors[f.key] && (
                                            <p className="tw-text-xs tw-text-red-500 tw-mt-1">{errors[f.key]}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    <section className="tw-bg-white tw-rounded tw-shadow tw-p-4">
                        <button
                            type="button"
                            className="tw-w-full tw-flex tw-items-center tw-justify-between tw-text-left tw-font-semibold tw-text-gray-900 tw-mb-3 tw-p-0 tw-bg-transparent tw-border-none tw-cursor-pointer hover:tw-bg-gray-50 tw-rounded tw-px-2 tw-py-1 group"
                            onClick={() => toggleSection("puzzle")}
                        >
                            Puzzle Info
                            <svg
                                className={`tw-w-5 tw-h-5 tw-transition-transform tw-duration-200 tw-select-none tw-text-gray-600 group-hover:tw-text-gray-800 ${expandedSections.puzzle ? "tw-rotate-180" : ""}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections.puzzle && (
                            <div className="tw-space-y-3">
                                {/* Creator Typeahead */}
                                <CreatorTypeahead
                                    value={form.selectedCreatorId || ""}
                                    selected={selectedCreator}
                                    onChange={(creatorId, creatorData) => {
                                        handleChange("selectedCreatorId", creatorId || "");
                                        setSelectedCreator(creatorId ? creatorData : null);
                                    }}
                                />
                                <div>
                                    <label
                                        htmlFor="postBody"
                                        className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1"
                                    >
                                        Post body
                                    </label>
                                    <textarea
                                        id="postBody"
                                        value={form.postBody || ""}
                                        onChange={(e) =>
                                            handleChange("postBody", e.target.value.slice(0, POST_BODY_MAX_LENGTH))
                                        }
                                        maxLength={POST_BODY_MAX_LENGTH}
                                        aria-describedby="postBody-character-count"
                                        className={getInputClasses("postBody", "tw-min-h-[120px]")}
                                    />
                                    <div
                                        id="postBody-character-count"
                                        aria-live="polite"
                                        className="tw-mt-1 tw-text-xs tw-text-gray-500 tw-text-right"
                                    >
                                        {(form.postBody || "").length}/{POST_BODY_MAX_LENGTH}
                                    </div>
                                </div>
                                {puzzleFields.map((f) => {
                                    const isPlayDate = f.key === "playDate";
                                    const isFeatureAgent = f.key === "featureAgent";
                                    const isEmailField =
                                        f.key === "emailSubject" || f.key === "emailImage" || f.key === "emailBody";
                                    const sendDailyEmailEnabled = form.sendDailyEmail === "true";
                                    const emailFieldDisabled = isEmailField && !sendDailyEmailEnabled;
                                    // When daily email is enabled, lock the time portion of playDate to 00:00:00
                                    const playTimeDisabled = sendDailyEmailEnabled;

                                    return (
                                        <div key={f.key}>
                                            {isFeatureAgent ? (
                                                <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={(form.featureAgent as string) === "true"}
                                                        onChange={(e) => {
                                                            handleChange(
                                                                "featureAgent",
                                                                e.target.checked ? "true" : "false",
                                                            );
                                                        }}
                                                        className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500"
                                                    />
                                                    <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                                                        {f.label}
                                                    </span>
                                                </label>
                                            ) : (
                                                <div>
                                                    {f.key === "emailSubject" && (
                                                        <label className="tw-flex tw-items-center tw-gap-2 tw-mb-2 tw-cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500"
                                                                checked={sendDailyEmailEnabled}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    handleChange(
                                                                        "sendDailyEmail",
                                                                        checked ? "true" : "false",
                                                                    );
                                                                    // When enabling daily email, normalize playDate to midnight (00:00:00)
                                                                    if (checked && form.playDate?.trim()) {
                                                                        const parsed = parseDateTime(form.playDate);
                                                                        if (parsed.date) {
                                                                            // Force time to 00:00:00 (12:00 AM)
                                                                            setPlayTime({
                                                                                hour: 12,
                                                                                minute: 0,
                                                                                ampm: "AM",
                                                                            });
                                                                            const midnight = combineDateAndTime(
                                                                                parsed.date,
                                                                                12,
                                                                                0,
                                                                                "AM",
                                                                            );
                                                                            handleChange("playDate", midnight);
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                            <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                                                                Send Daily Email Notification
                                                            </span>
                                                        </label>
                                                    )}

                                                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                                                        {f.label}
                                                        {f.required && <span className="tw-text-red-500">*</span>}
                                                    </label>
                                                    {isPlayDate ? (
                                                        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                                                            <div className="tw-flex-1 tw-min-w-[200px]">
                                                                <DatePicker
                                                                    selected={parseDateTime(form.playDate).date}
                                                                    onChange={(date) => {
                                                                        if (date) {
                                                                            // Always combine date with current time picker values
                                                                            const dateTimeStr = combineDateAndTime(
                                                                                date,
                                                                                playTime.hour,
                                                                                playTime.minute,
                                                                                playTime.ampm,
                                                                            );
                                                                            handleChange("playDate", dateTimeStr);
                                                                        } else {
                                                                            handleChange("playDate", "");
                                                                        }
                                                                    }}
                                                                    minDate={new Date()}
                                                                    dateFormat="MM/dd/yyyy"
                                                                    placeholderText="Pick a date"
                                                                    className={getInputClasses(f.key)}
                                                                    showIcon
                                                                    icon={
                                                                        <svg
                                                                            className="tw-w-5 tw-h-5 tw-text-gray-400"
                                                                            fill="none"
                                                                            stroke="currentColor"
                                                                            viewBox="0 0 24 24"
                                                                        >
                                                                            <path
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                                strokeWidth={2}
                                                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                                            />
                                                                        </svg>
                                                                    }
                                                                    wrapperClassName="tw-w-full"
                                                                />
                                                            </div>

                                                            <div className="hour-dropdown-container tw-relative tw-border tw-border-gray-300">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (playTimeDisabled) return;
                                                                        setShowHourDropdown(!showHourDropdown);
                                                                    }}
                                                                    className={clsx(
                                                                        getInputClasses("playTime"),
                                                                        "tw-w-16 tw-text-center",
                                                                        playTimeDisabled
                                                                            ? "tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed"
                                                                            : "tw-bg-white tw-text-black",
                                                                    )}
                                                                    disabled={playTimeDisabled}
                                                                >
                                                                    {playTime.hour}
                                                                </button>
                                                                {showHourDropdown && (
                                                                    <div className="tw-absolute tw-top-full tw-left-0 tw-mt-1 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md tw-shadow-lg tw-z-50 tw-max-h-40 tw-overflow-y-auto tw-w-16">
                                                                        {Array.from(
                                                                            { length: 12 },
                                                                            (_, i) => i + 1,
                                                                        ).map((hour) => (
                                                                            <button
                                                                                key={hour}
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setPlayTime((prev) => ({
                                                                                        ...prev,
                                                                                        hour,
                                                                                    }));
                                                                                    setShowHourDropdown(false);
                                                                                    setPristine(false);
                                                                                }}
                                                                                className={clsx(
                                                                                    "tw-block tw-w-full tw-px-3 tw-py-2 tw-text-center tw-text-sm tw-text-black hover:tw-bg-gray-100",
                                                                                    hour === playTime.hour
                                                                                        ? "tw-bg-gray-100"
                                                                                        : "tw-bg-white",
                                                                                )}
                                                                            >
                                                                                {hour}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <span className="tw-text-gray-500 tw-font-bold">:</span>
                                                            <div className="minute-dropdown-container tw-relative tw-border tw-border-gray-300">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (playTimeDisabled) return;
                                                                        setShowMinuteDropdown(!showMinuteDropdown);
                                                                    }}
                                                                    className={clsx(
                                                                        getInputClasses("playTime"),
                                                                        "tw-w-16 tw-text-center",
                                                                        playTimeDisabled
                                                                            ? "tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed"
                                                                            : "tw-bg-white tw-text-black",
                                                                    )}
                                                                    disabled={playTimeDisabled}
                                                                >
                                                                    {playTime.minute.toString().padStart(2, "0")}
                                                                </button>
                                                                {showMinuteDropdown && (
                                                                    <div className="tw-absolute tw-top-full tw-left-0 tw-mt-1 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md tw-shadow-lg tw-z-50 tw-max-h-40 tw-overflow-y-auto tw-w-16">
                                                                        {Array.from({ length: 60 }, (_, i) => i).map(
                                                                            (minute) => (
                                                                                <button
                                                                                    key={minute}
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        setPlayTime((prev) => ({
                                                                                            ...prev,
                                                                                            minute,
                                                                                        }));
                                                                                        setShowMinuteDropdown(false);
                                                                                        setPristine(false);
                                                                                    }}
                                                                                    className={clsx(
                                                                                        "tw-block tw-w-full tw-px-3 tw-py-2 tw-text-center tw-text-sm tw-text-black hover:tw-bg-gray-100",
                                                                                        minute === playTime.minute
                                                                                            ? "tw-bg-gray-100"
                                                                                            : "tw-bg-white",
                                                                                    )}
                                                                                >
                                                                                    {minute.toString().padStart(2, "0")}
                                                                                </button>
                                                                            ),
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="tw-flex tw-gap-1">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (playTimeDisabled) return;
                                                                        setPlayTime((prev) => ({
                                                                            ...prev,
                                                                            ampm: "AM",
                                                                        }));
                                                                        setPristine(false);
                                                                    }}
                                                                    className={clsx(
                                                                        "tw-px-3 tw-py-2 tw-text-xs tw-rounded tw-font-medium tw-transition-colors",
                                                                        playTime.ampm === "AM"
                                                                            ? "tw-bg-blue-500 tw-text-white"
                                                                            : "tw-bg-gray-200 tw-text-gray-700 hover:tw-bg-gray-300",
                                                                    )}
                                                                >
                                                                    AM
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (playTimeDisabled) return;
                                                                        setPlayTime((prev) => ({
                                                                            ...prev,
                                                                            ampm: "PM",
                                                                        }));
                                                                        setPristine(false);
                                                                    }}
                                                                    className={clsx(
                                                                        "tw-px-3 tw-py-2 tw-text-xs tw-rounded tw-font-medium tw-transition-colors",
                                                                        playTime.ampm === "PM"
                                                                            ? "tw-bg-blue-500 tw-text-white"
                                                                            : "tw-bg-gray-200 tw-text-gray-700 hover:tw-bg-gray-300",
                                                                    )}
                                                                >
                                                                    PM
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : f.multiline ? (
                                                        <textarea
                                                            value={form[f.key] || ""}
                                                            onChange={(e) => handleChange(f.key, e.target.value)}
                                                            placeholder={isAddMode ? f.label : undefined}
                                                            className={getInputClasses(
                                                                f.key,
                                                                `tw-min-h-[90px] ${
                                                                    emailFieldDisabled
                                                                        ? "tw-bg-gray-100 tw-cursor-not-allowed"
                                                                        : ""
                                                                }`,
                                                            )}
                                                            disabled={emailFieldDisabled}
                                                        />
                                                    ) : (
                                                        <input
                                                            type={
                                                                f.key === "emailImage" || f.key === "imageIndex"
                                                                    ? "number"
                                                                    : "text"
                                                            }
                                                            value={form[f.key] || ""}
                                                            onChange={(e) => handleChange(f.key, e.target.value)}
                                                            placeholder={isAddMode ? f.label : undefined}
                                                            className={getInputClasses(
                                                                f.key,
                                                                emailFieldDisabled
                                                                    ? "tw-bg-gray-100 tw-cursor-not-allowed"
                                                                    : undefined,
                                                            )}
                                                            disabled={emailFieldDisabled}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            {errors[f.key] && (
                                                <p className="tw-text-xs tw-text-red-500 tw-mt-1">{errors[f.key]}</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    <section className="tw-bg-white tw-rounded tw-shadow tw-p-4 md:tw-col-span-2">
                        <button
                            type="button"
                            className="tw-w-full tw-flex tw-items-center tw-justify-between tw-text-left tw-font-semibold tw-text-gray-900 tw-mb-3 tw-p-0 tw-bg-transparent tw-border-none tw-cursor-pointer hover:tw-bg-gray-50 tw-rounded tw-px-2 tw-py-1 group"
                            onClick={() => toggleSection("agent")}
                        >
                            Agent Info
                            <svg
                                className={`tw-w-5 tw-h-5 tw-transition-transform tw-duration-200 tw-select-none tw-text-gray-600 group-hover:tw-text-gray-800 ${expandedSections.agent ? "tw-rotate-180" : ""}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections.agent && (
                            <div className="tw-space-y-4">
                                {/* Agent Typeahead */}
                                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                    <AgentTypeahead
                                        value={form.selectedAgentId || ""}
                                        onChange={(agentId, agentData) => {
                                            handleChange("selectedAgentId", agentId);
                                            handleChange("selectedAgentAuth0UserId", agentData?.auth0UserId || "");
                                            if (agentData) {
                                                // Auto-fill agent fields
                                                handleChange("agentFirstName", agentData.firstName || "");
                                                handleChange("agentLastName", agentData.lastName || "");
                                                handleChange("agentEmail", agentData.email || "");
                                                handleChange("agentPhoneNumber", agentData.phoneNumber || "");
                                                handleChange("agentBrokerage", agentData.brokerage || "");
                                                handleChange("agentPrimaryLink", agentData.link || "");
                                                handleChange("agentImageLink", agentData.img || "");
                                                handleChange("agentInstagram", agentData.instagram || "");
                                                handleChange("agentTiktok", agentData.tiktok || "");
                                                handleChange("agentTwitter", agentData.xTwitter || "");
                                                handleChange("agentFacebook", agentData.facebook || "");
                                                handleChange("agentYoutube", agentData.youtube || "");
                                                handleChange("agentMlsId", agentData.mlsId || "");
                                                setShowAgentFields(true);
                                            }
                                        }}
                                        onToggleNewAgent={() => setShowAgentFields(!showAgentFields)}
                                    />
                                </div>
                                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                    <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={form.sendClaimAccountLink === "true"}
                                            disabled={isSendClaimAccountLinkDisabled}
                                            onChange={(e) => {
                                                handleChange(
                                                    "sendClaimAccountLink",
                                                    e.target.checked ? "true" : "false",
                                                );
                                                if (e.target.checked) {
                                                    handleChange("sendPasswordReset", "false");
                                                }
                                            }}
                                            className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                        />
                                        <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                                            Send link to claim account
                                        </span>
                                    </label>
                                    <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={form.sendPasswordReset === "true"}
                                            disabled={isSendPasswordResetDisabled}
                                            onChange={(e) => {
                                                handleChange("sendPasswordReset", e.target.checked ? "true" : "false");
                                                if (e.target.checked) {
                                                    handleChange("sendClaimAccountLink", "false");
                                                }
                                            }}
                                            className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                        />
                                        <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                                            Send password reset
                                        </span>
                                    </label>
                                </div>
                                {showAgentFields && (
                                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                        {agentFields
                                            .filter((k) => k.key !== "agentImageLink")
                                            .map((f) => (
                                                <div key={f.key}>
                                                    <>
                                                        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                                                            {f.label}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={form[f.key] || ""}
                                                            onChange={(e) => handleChange(f.key, e.target.value)}
                                                            placeholder={
                                                                isAddMode ? f.label.replace("Agent ", "") : undefined
                                                            }
                                                            className={getInputClasses(f.key)}
                                                        />
                                                        {errors[f.key] && (
                                                            <p className="tw-text-xs tw-text-red-500 tw-mt-1">
                                                                {errors[f.key]}
                                                            </p>
                                                        )}
                                                    </>
                                                </div>
                                            ))}
                                        <div>
                                            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                                                Upload Agent Image
                                            </label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;
                                                    // Store agent image in state for both add and edit modes
                                                    setAgentImage(file);
                                                    setPristine(false);
                                                    e.target.value = "";
                                                }}
                                                className="tw-hidden"
                                                id="agent-image-upload"
                                            />
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                {(agentImage || form.agentImageLink) && (
                                                    <div className="tw-flex-shrink-0">
                                                        <img
                                                            src={
                                                                agentImagePreviewUrl ||
                                                                (form.agentImageLink.startsWith("http")
                                                                    ? form.agentImageLink
                                                                    : `${form.agentImageLink.replace(/^\/+/, "")}`)
                                                            }
                                                            alt="Agent preview"
                                                            className="tw-w-10 tw-h-10 tw-object-cover tw-rounded-full tw-border-2 tw-border-gray-300"
                                                            onError={(e) => {
                                                                (e.target as HTMLImageElement).style.display = "none";
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                <input
                                                    type="text"
                                                    value={
                                                        agentImage
                                                            ? agentImage.name
                                                            : form.agentImageLink
                                                              ? form.agentImageLink.split("/").pop() ||
                                                                form.agentImageLink
                                                              : ""
                                                    }
                                                    onClick={() => {
                                                        document.getElementById("agent-image-upload")?.click();
                                                    }}
                                                    placeholder="Click to upload agent image (saved to agents-img)"
                                                    readOnly
                                                    className={clsx(
                                                        getInputClasses("agentImageUpload"),
                                                        "tw-cursor-pointer tw-flex-1",
                                                    )}
                                                />
                                                {((isAddMode && agentImage) || form.agentImageLink) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setAgentImage(null);
                                                            handleChange("agentImageLink", "");
                                                            setPristine(false);
                                                        }}
                                                        className="tw-bg-red-500 hover:tw-bg-red-600 tw-text-white tw-px-3 tw-py-2 tw-rounded-md tw-text-sm"
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                </div>

                <div className="tw-flex tw-items-center tw-justify-between tw-mt-6">
                    <div className="tw-flex tw-gap-3">
                        <CustomButton
                            className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white"
                            handleClick={handleSubmit}
                            text={
                                (updatePropertyMutation as any).isPending || (createPropertyMutation as any).isPending
                                    ? "Submitting..."
                                    : "Submit"
                            }
                            id="property-editor-submit"
                            dataCy="property-editor-submit"
                            isLoading={
                                (updatePropertyMutation as any).isPending || (createPropertyMutation as any).isPending
                            }
                            isDisabled={
                                (isAddMode && pristine) ||
                                (updatePropertyMutation as any).isPending ||
                                (createPropertyMutation as any).isPending
                            }
                        />
                        <CustomButton
                            className="tw-bg-gray-500 hover:tw-bg-gray-600 tw-text-white"
                            handleClick={handleReset}
                            text="Reset"
                            id="property-editor-reset"
                            dataCy="property-editor-reset"
                            isDisabled={pristine}
                        />
                    </div>

                    {!isAddMode && (
                        <button
                            type="button"
                            className="tw-bg-red-600 hover:tw-bg-red-700 tw-text-white tw-font-medium tw-py-2 tw-px-4 tw-rounded-md tw-border tw-border-red-600 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                            onClick={() => setShowRemoveModal(true)}
                            disabled={deletePropertyMutation.isPending || !playablePropertyId}
                            title="Remove this daily property"
                        >
                            Remove Daily Property
                        </button>
                    )}
                </div>

                {showRemoveModal && (
                    <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center">
                        <div className="tw-bg-white tw-rounded tw-shadow tw-w-full tw-max-w-md tw-p-5">
                            <h3 className="tw-text-lg tw-font-semibold tw-mb-2">Confirm Removal</h3>
                            <p className="tw-text-sm tw-text-gray-700 tw-mb-4">
                                Are you sure you want to remove this property from its scheduled date? This will not
                                delete the listing.
                            </p>
                            <div className="tw-flex tw-justify-end tw-gap-2">
                                <button
                                    className="tw-bg-gray-200 hover:tw-bg-gray-300 tw-text-gray-800 tw-px-3 tw-py-1 tw-rounded"
                                    onClick={() => setShowRemoveModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="tw-bg-red-600 hover:tw-bg-red-700 tw-text-white tw-px-3 tw-py-1 tw-rounded disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                    onClick={() => {
                                        if (playablePropertyId) {
                                            deletePropertyMutation.mutate(playablePropertyId);
                                        }
                                    }}
                                    disabled={deletePropertyMutation.isPending || !playablePropertyId}
                                >
                                    {deletePropertyMutation.isPending ? "Removing..." : "Yes, remove"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Images Modal */}
                {showImagesModal && (
                    <div
                        className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-[9999]"
                        style={{ zIndex: 10000 }}
                    >
                        <div className="tw-bg-white tw-rounded-lg tw-shadow tw-w-full tw-max-w-4xl tw-max-h-[85vh] tw-overflow-hidden tw-flex tw-flex-col">
                            <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b tw-flex-shrink-0">
                                <h3 className="tw-text-lg tw-font-semibold">Images ({imageItems.length})</h3>
                                <button
                                    onClick={() => setShowImagesModal(false)}
                                    className="tw-text-gray-500 hover:tw-text-gray-700 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-full tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center tw-text-xl"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="tw-p-3 tw-flex-1 tw-overflow-y-auto">
                                <div className="tw-w-full">
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleFileInput}
                                        className="tw-hidden"
                                        id="modal-image-upload"
                                    />
                                    <div
                                        id="images-container"
                                        className={clsx(
                                            "tw-border-2 tw-border-dashed tw-rounded-lg tw-p-4 tw-text-center tw-transition-colors tw-min-h-[300px] tw-flex tw-flex-col tw-items-start",
                                            dragActive
                                                ? "tw-border-blue-400 tw-bg-blue-50"
                                                : "tw-border-gray-300 hover:tw-border-gray-400",
                                        )}
                                        onDragEnter={handleDrag}
                                        onDragLeave={handleDrag}
                                        onDragOver={handleDrag}
                                        onDrop={handleDrop}
                                    >
                                        {imageItems.length === 0 ? (
                                            <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-text-gray-500">
                                                <div className="tw-text-center">
                                                    <svg
                                                        className="tw-mx-auto tw-h-12 tw-w-12 tw-text-gray-400 tw-mb-4"
                                                        stroke="currentColor"
                                                        fill="none"
                                                        viewBox="0 0 48 48"
                                                    >
                                                        <path
                                                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                                            strokeWidth={2}
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                    <p className="tw-text-center">
                                                        Drag and drop images here
                                                        <br />
                                                        <span className="tw-text-sm tw-text-gray-400">
                                                            PNG, JPG, GIF up to 10MB
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="tw-w-full">
                                                <DndContext
                                                    sensors={sensors}
                                                    collisionDetection={closestCenter}
                                                    onDragEnd={handleDragEnd}
                                                >
                                                    <SortableContext
                                                        items={imageItemIds}
                                                        strategy={rectSortingStrategy}
                                                    >
                                                        <div className="tw-grid tw-grid-cols-3 md:tw-grid-cols-5 tw-gap-3">
                                                            {imageItems.map((item, index) => (
                                                                <SortableItem
                                                                    key={item.id}
                                                                    item={item}
                                                                    index={index}
                                                                    imageItems={imageItems}
                                                                    onRemove={removeImageItem}
                                                                    onMove={moveImageItem}
                                                                    propertyId={propertyId}
                                                                    deleteImageMutation={deleteImageMutation}
                                                                />
                                                            ))}
                                                        </div>
                                                    </SortableContext>
                                                </DndContext>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="tw-flex tw-items-center tw-justify-between tw-py-2 tw-px-4 tw-border-t tw-flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={() => document.getElementById("modal-image-upload")?.click()}
                                    className="tw-text-gray-500 hover:tw-text-gray-700 tw-bg-gray-100 hover:tw-bg-gray-200 tw-w-full tw-h-10 tw-flex tw-items-center tw-justify-center tw-text-xl"
                                >
                                    Add more images ({imageItems.length})
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Clear All Images Confirmation Modal */}
                {showClearAllModal && (
                    <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50">
                        <div className="tw-bg-white tw-rounded tw-shadow tw-w-full tw-max-w-md tw-p-5">
                            <h3 className="tw-text-lg tw-font-semibold tw-mb-2">Clear All Images</h3>
                            <p className="tw-text-sm tw-text-gray-700 tw-mb-4">Are you sure? This can't be undone</p>
                            <div className="tw-flex tw-justify-end tw-gap-2">
                                <button
                                    className="tw-bg-gray-200 hover:tw-bg-gray-300 tw-text-gray-800 tw-px-3 tw-py-1 tw-rounded"
                                    onClick={() => setShowClearAllModal(false)}
                                >
                                    No
                                </button>
                                <button
                                    className="tw-bg-red-600 hover:tw-bg-red-700 tw-text-white tw-px-3 tw-py-1 tw-rounded"
                                    onClick={() => {
                                        setImageItems((prev) => prev.filter((i) => i.type === "existing"));
                                        setShowClearAllModal(false);
                                        setPristine(false);
                                    }}
                                >
                                    Yes
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Agent Image Upload Loading Modal */}
                {isUploadingAgentImage && (
                    <div className="tw-fixed tw-inset-0 tw-bg-black/60 tw-flex tw-items-center tw-justify-center tw-z-[9999]">
                        <div className="tw-text-center">
                            {/* <MoonLoader size={40} color="#63c19f" /> */}
                            <p className="tw-mt-4 tw-text-white tw-text-lg">Uploading agent image...</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
